import moment from "moment";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { GetAllCsvData } from "../../redux/reducers/EventReducer";
import FullPageLoader from "./FullPageLoader";
import { useState } from "react";
import { stringify } from "csv-stringify";
import { useDispatch, useSelector } from "react-redux";
import { generateToast } from "../../helper";
import { ERROR } from "../../constant";
import { convertNumToFormatString } from "../../helper/CostHelpers";

const styles = {
  container: {
    display: "inline-flex",
    alignItems: "center",
  },
  buttonGroup: {
    boxShadow: "none",
  },
  button: (isDark) => ({
    height: "2.1rem",
    fontSize: "11px",
    textTransform: "none",
    fontWeight: 500,
    padding: "4px 12px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.23)" : "#2196F3"}`,
    backgroundColor: "transparent",
    whiteSpace: "nowrap",
  }),
  icon: {
    color: "#2196F3",
    fontSize: "18px",
    marginRight: "4px",
  },
  label: (isDark) => ({
    color: isDark ? "white" : "#2196F3",
  }),
};

const CsvExport = ({ mode }) => {
  const Headers = [
    "Created At",
    "Event Date",
    "Client Name",
    "Client Type",
    "School/Region",
    "Sold By",
    "Created By",
    "Amount",
    "Cost",
    "Profit",
    "Profit %",
    "Status",
    "Sign Date",
    "Invoiced?",
  ];
  const dispatch = useDispatch();
  const [csvLoading, setCsvLoading] = useState(false);
  const {
    Contracts: { filter },
  } = useSelector((state) => state);

  const isDark = mode === "dark";

  const handleExport = async (filterParam) => {
    try {
      setCsvLoading(true);
      const csvDataPayload = await dispatch(GetAllCsvData(filterParam));

      if (csvDataPayload.type.endsWith("/rejected")) {
        throw new Error(csvDataPayload.error?.message || "Failed to fetch data");
      }

      const AllCsvData = csvDataPayload?.payload;
      if (!AllCsvData || !AllCsvData.data) {
        throw new Error("No data received from server");
      }

      const csvData = AllCsvData?.data?.slice(1);
      if (!csvData || csvData.length === 0) {
        generateToast("No data available to export", ERROR);
        setCsvLoading(false);
        return;
      }

      const CSVFileName = filterParam
        ? `TPE-Filtered-Contract-${filterParam.filter.startEventDate || "N/A"}-${filterParam.filter.endEventDate || "N/A"}.csv`
        : "TPE-CONTRACTS-ALL.csv";

      const formattedData = csvData?.map((event, index) => {
        try {
          // Column indices match backend generateCSV output:
          // 0: createdAt, 1: startDate, 2: clientName, 3: clientType, 4: schoolRegion,
          // 5: soldBy, 6: createdBy, 7: clientBudget, 8: totalCost, 9: profit,
          // 10: profitMargin, 11: contractStatus, 12: signDate, 13: isInvoiced
          return [
            event?.[0] ? moment(event[0]).format("MM/DD/YYYY") : "N/A",
            event?.[1] ? moment(event[1]).format("MM/DD/YYYY") : "N/A",
            event?.[2] || "N/A",
            event?.[3] || "N/A",
            event?.[4] || "N/A",
            event?.[5] || "N/A",
            event?.[6] || "N/A",
            `$${convertNumToFormatString(event?.[7] || 0)}`,
            `$${convertNumToFormatString(event?.[8] || 0)}`,
            `$${convertNumToFormatString(event?.[9] || 0)}`,
            event?.[10] ? (event[10] * 100).toFixed(2) + "%" : "0.00%",
            event?.[11] || "N/A",
            event?.[12] ? moment(event[12]).format("MM/DD/YYYY") : "Not Signed",
            event?.[13] === true || event?.[13] === "true" ? "Yes" : "No",
          ];
        } catch (rowError) {
          console.error(`Error processing row ${index}:`, rowError);
          return Array(14).fill("ERROR");
        }
      });

      stringify([Headers, ...formattedData], (err, output) => {
        if (err) {
          generateToast("Failed to generate CSV file", ERROR);
          setCsvLoading(false);
          return;
        }

        try {
          const blob = new Blob([output], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", CSVFileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          generateToast("Data exported successfully!", "success");
        } catch (downloadError) {
          generateToast("Failed to download file", ERROR);
        } finally {
          setCsvLoading(false);
        }
      });
    } catch (error) {
      console.error("Export error:", error);
      generateToast(error.message || "Failed to export data. Please try again.", ERROR);
      setCsvLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <ButtonGroup variant="outlined" size="small" style={styles.buttonGroup}>
        <Button
          style={styles.button(isDark)}
          onClick={() => handleExport()}
          disabled={csvLoading}
        >
          <FileDownloadIcon style={styles.icon} />
          <span style={styles.label(isDark)}>All Data (CSV)</span>
        </Button>
        <Button
          style={styles.button(isDark)}
          onClick={() => handleExport({ filter: { ...filter, hasTitle: false } })}
          disabled={csvLoading}
        >
          <FileDownloadIcon style={styles.icon} />
          <span style={styles.label(isDark)}>Filtered (CSV)</span>
        </Button>
      </ButtonGroup>
      {csvLoading && <FullPageLoader />}
    </div>
  );
};

export default CsvExport;
